import ApiService from './ApiService';
import WorkspaceContextService from '@services/WorkspaceContextService';

export interface Workspace {
  workspaceId: string;
  companyName: string;
  displayName: string;
  parentCompany: string;
  createdBy: string;
  createdAt: string;
  isActive: boolean;
  role: string | null;
  onboardingStep?: number;
  isOnboarded?: boolean;
  onboardedAt?: string;
}

export interface CreateWorkspaceRequest {
  displayName: string;
  companyUrl?: string;
  addAllStaff?: boolean;
}

export interface AISettings {
  leoModelChain: string;
  executiveIntelligenceModelChain: string;
  availableChains: Record<string, string>;
}

export interface UpdateAISettings {
  leoModelChain?: string;
  executiveIntelligenceModelChain?: string;
}

/** Company-wide Home "Data and Insights" tile layout. `order` is the tile keys
 * in display order; `hidden` are removed tiles. */
export interface DataInsightsLayout {
  order: string[];
  hidden: string[];
}

export interface OnboardWorkspaceRequest {
  companyUrl: string;
  skipBranding?: boolean;
  skipPersonas?: boolean;
  skipAI?: boolean;
}

export interface OnboardingStepRequest {
  step: number;
}

class WorkspaceService {
  /**
   * Check if a workspace name already exists
   */
  async checkWorkspaceName(displayName: string): Promise<{ exists: boolean }> {
    const { success, data, error } = await ApiService.post<{ displayName: string }, { exists: boolean }>(
      '/workspaces/check-name',
      { displayName }
    );
    if (!success || !data) {
      throw new Error(error || 'Failed to check workspace name');
    }
    return data;
  }

  /**
   * Get all workspaces the user has access to
   */
  async getWorkspaces(): Promise<Workspace[]> {
    const { success, data, error } = await ApiService.get<Workspace[]>('/workspaces');
    if (!success || !data) {
      throw new Error(error || 'Failed to fetch workspaces');
    }
    return data;
  }

  /**
   * Create a new workspace
   */
  async createWorkspace(data: CreateWorkspaceRequest): Promise<Workspace> {
    const { success, data: workspace, error } = await ApiService.post<CreateWorkspaceRequest, Workspace>('/workspaces', data);
    if (!success || !workspace) {
      throw new Error(error || 'Failed to create workspace');
    }
    return workspace;
  }

  /**
   * Get workspace details by ID
   */
  async getWorkspace(workspaceId: string): Promise<Workspace> {
    const { success, data, error } = await ApiService.get<Workspace>(`/workspaces/${workspaceId}`);
    if (!success || !data) {
      throw new Error(error || 'Failed to fetch workspace');
    }
    return data;
  }

  /**
   * Delete (deactivate) a workspace. Only the owner can delete.
   */
  async deleteWorkspace(workspaceId: string): Promise<{ message: string }> {
    const { success, data, error } = await ApiService.delete<{ message: string }>(`/workspaces/${workspaceId}`);
    if (!success || !data) {
      throw new Error(error || 'Failed to delete workspace');
    }
    return data;
  }

  /**
   * Update workspace settings
   */
  async updateWorkspace(workspaceId: string, updates: Partial<Workspace>): Promise<Workspace> {
    const { success, data, error } = await ApiService.put<Partial<Workspace>, Workspace>(`/workspaces/${workspaceId}`, updates);
    if (!success || !data) {
      throw new Error(error || 'Failed to update workspace');
    }
    return data;
  }

  /**
   * Switch to a workspace context.
   *
   * Pins this tab in sessionStorage so subsequent requests carry the
   * X-Workspace-Id header for the new workspace immediately. The
   * server-side cookie the endpoint sets remains the "default for new
   * tabs." The pin auto-captures the current userId from USER_PROFILE
   * so a later user swap on this tab can detect and clear it.
   */
  async switchWorkspace(workspaceId: string): Promise<{ message: string; workspace: Workspace }> {
    const { success, data, error } = await ApiService.put<undefined, { message: string; workspace: Workspace }>(
      `/workspaces/${workspaceId}/switch`,
      undefined
    );
    if (!success || !data) {
      throw new Error(error || 'Failed to switch workspace');
    }
    WorkspaceContextService.setWorkspace(
      data.workspace.workspaceId,
      data.workspace.companyName,
    );
    return data;
  }

  /**
   * Return to home company context (clear workspace).
   *
   * Pins this tab explicitly to home so subsequent requests override
   * whatever the shared cookie says. `homeCompanyName` may be omitted;
   * if empty, the next /me response repairs COMPANY_KEY via
   * WorkspaceContextService.syncCompanyName.
   */
  async clearWorkspace(homeCompanyName?: string): Promise<{ message: string }> {
    const { success, data, error } = await ApiService.put<undefined, { message: string }>('/workspaces/clear', undefined);
    if (!success || !data) {
      throw new Error(error || 'Failed to clear workspace');
    }
    WorkspaceContextService.setHome(homeCompanyName ?? '');
    return data;
  }

  /**
   * Trigger workspace onboarding (AI, personas, branding)
   * Call this AFTER creating the workspace and collecting the company URL
   */
  async onboardWorkspace(
    workspaceId: string,
    requestData: OnboardWorkspaceRequest
  ): Promise<{
    message: string;
    workspaceId: string;
    companyName: string;
    companyUrl: string;
    status: string;
    skipped: { ai: boolean; personas: boolean; branding: boolean };
  }> {
    const { success, data, error } = await ApiService.post<OnboardWorkspaceRequest, {
      message: string;
      workspaceId: string;
      companyName: string;
      companyUrl: string;
      status: string;
      skipped: { ai: boolean; personas: boolean; branding: boolean };
    }>(`/workspaces/${workspaceId}/onboard`, requestData);
    if (!success || !data) {
      throw new Error(error || 'Failed to onboard workspace');
    }
    return data;
  }

  /**
   * Update workspace onboarding step
   */
  async updateOnboardingStep(
    workspaceId: string,
    step: number
  ): Promise<{ message: string; onboardingStep: number }> {
    const { success, data, error } = await ApiService.put<OnboardingStepRequest, { message: string; onboardingStep: number }>(
      `/workspaces/${workspaceId}/onboarding/step`,
      { step }
    );
    if (!success || !data) {
      throw new Error(error || 'Failed to update onboarding step');
    }
    return data;
  }

  /**
   * Mark workspace onboarding as complete
   */
  async completeOnboarding(
    workspaceId: string
  ): Promise<{ message: string; isOnboarded: boolean; onboardedAt?: string }> {
    const { success, data, error } = await ApiService.post<undefined, { message: string; isOnboarded: boolean; onboardedAt?: string }>(
      `/workspaces/${workspaceId}/onboarding/complete`,
      undefined
    );
    if (!success || !data) {
      throw new Error(error || 'Failed to complete onboarding');
    }
    return data;
  }

  /**
   * Get AI model settings for the current company
   */
  async getAISettings(): Promise<AISettings> {
    const { success, data, error } = await ApiService.get<AISettings>(
      '/workspaces/ai-settings'
    );
    if (!success || !data) {
      throw new Error(error || 'Failed to fetch AI settings');
    }
    return data;
  }

  /**
   * Update one or both AI model-chain settings
   */
  async updateAISettings(settings: UpdateAISettings): Promise<AISettings> {
    const { success, data, error } = await ApiService.put<
      UpdateAISettings,
      AISettings
    >('/workspaces/ai-settings', settings);
    if (!success || !data) {
      throw new Error(error || 'Failed to update AI settings');
    }
    return data;
  }

  /**
   * Update the company's Home "Data and Insights" tile layout (admin-only).
   */
  async updateDataInsightsLayout(
    layout: DataInsightsLayout,
  ): Promise<DataInsightsLayout> {
    const { success, data, error } = await ApiService.put<
      DataInsightsLayout,
      DataInsightsLayout
    >('/workspaces/data-insights-layout', layout);
    if (!success || !data) {
      throw new Error(error || 'Failed to update layout');
    }
    return data;
  }

  /**
   * Get workspace members
   */
  async getMembers(workspaceId: string): Promise<any[]> {
    const { success, data, error } = await ApiService.get<any[]>(`/workspaces/${workspaceId}/members`);
    if (!success || !data) {
      throw new Error(error || 'Failed to fetch workspace members');
    }
    return data;
  }

  /**
   * Add a member to workspace
   */
  async addMember(
    workspaceId: string,
    userId: string,
    role: string = 'editor'
  ): Promise<any> {
    const { success, data, error } = await ApiService.post<{ userId: string; role: string }, any>(
      `/workspaces/${workspaceId}/members`,
      { userId, role }
    );
    if (!success || !data) {
      throw new Error(error || 'Failed to add member');
    }
    return data;
  }

  /**
   * Remove a member from workspace
   */
  async removeMember(workspaceId: string, userId: string): Promise<{ message: string }> {
    const { success, data, error } = await ApiService.delete<{ message: string }>(`/workspaces/${workspaceId}/members/${userId}`);
    if (!success || !data) {
      throw new Error(error || 'Failed to remove member');
    }
    return data;
  }

  /**
   * Update a member's role in a workspace
   */
  async updateMemberRole(
    workspaceId: string,
    userId: string,
    role: string,
  ): Promise<any> {
    const { success, data, error } = await ApiService.patch<{ role: string }, any>(
      `/workspaces/${workspaceId}/members/${userId}`,
      { role },
    );
    if (!success || !data) {
      throw new Error(error || 'Failed to update member role');
    }
    return data;
  }

  /**
   * Get team members from parent company
   */
  async getTeam(): Promise<any[]> {
    const { success, data, error } = await ApiService.get<any[]>('/workspaces/team');
    if (!success || !data) {
      throw new Error(error || 'Failed to fetch team');
    }
    return data;
  }
}

export default new WorkspaceService();
